
import { AggregatedSessionV2 } from "@global/types/session";
import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { SingleSegmentSelector } from "../components/segment-selector";
import { useDataStore, useFilters } from "../context";
import {
  AcquisitionSource,
  DEFAULT_SEGMENTS,
  Segment,
} from "../types/analytics";
import { filterSessionsBySegment } from "./overview/analytics";

// ============================================================================
// TYPES
// ============================================================================

type AcquisitionView = "source" | "country" | "device";
type SortKey = keyof Pick<
  AcquisitionSource,
  "sessions" | "engagedSessionRate" | "ctaConversionRate"
>;
type SortDirection = "asc" | "desc";

// ============================================================================
// CHART COLORS
// ============================================================================

const CHART_COLORS = [
  "#1976d2",
  "#dc004e",
  "#388e3c",
  "#f57c00",
  "#7b1fa2",
  "#00796b",
  "#c2185b",
  "#303f9f",
];

// ============================================================================
// SOURCE ROW COMPONENT
// ============================================================================

interface SourceRowProps {
  source: AcquisitionSource;
  maxSessions: number;
}

const SourceRow: React.FC<SourceRowProps> = ({ source, maxSessions }) => {
  const theme = useTheme();

  return (
    <TableRow hover>
      <TableCell>
        <Typography
          variant="body2"
          sx={{
            maxWidth: 250,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {source.source || "(direct)"}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <Box sx={{ width: 80, mr: 1 }}>
            <LinearProgress
              variant="determinate"
              value={
                maxSessions > 0 ? (source.sessions / maxSessions) * 100 : 0
              }
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
          {source.sessions.toLocaleString()}
        </Box>
      </TableCell>
      <TableCell align="right">
        <Chip
          label={`${(source.engagedSessionRate * 100).toFixed(1)}%`}
          size="small"
          color={source.engagedSessionRate > 0.5 ? "success" : "default"}
          variant="outlined"
        />
      </TableCell>
      <TableCell align="right">
        <Typography
          variant="body2"
          sx={{
            color:
              source.ctaConversionRate > 0.05
                ? theme.palette.success.main
                : theme.palette.text.primary,
            fontWeight: source.ctaConversionRate > 0.05 ? 600 : 400,
          }}
        >
          {(source.ctaConversionRate * 100).toFixed(2)}%
        </Typography>
      </TableCell>
    </TableRow>
  );
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function extractAcquisitionDataFromSessions(
  sessions: AggregatedSessionV2[],
  view: AcquisitionView
): AcquisitionSource[] {
  if (sessions.length === 0) return [];

  const sourceData: Record<
    string,
    { sessions: number; conversions: number; engaged: number }
  > = {};

  sessions.forEach((session) => {
    let sourceKey = "unknown";

    switch (view) {
      case "source":
        sourceKey = session.referrer || "(direct)";
        // Extract domain from referrer
        if (session.referrer && session.referrer !== "(direct)") {
          try {
            const url = new URL(
              session.referrer.startsWith("http")
                ? session.referrer
                : `https://${session.referrer}`
            );
            sourceKey = url.hostname;
          } catch {
            sourceKey = session.referrer;
          }
        }
        break;
      case "country":
        sourceKey = session.country || "Unknown";
        break;
      case "device": {
        const width = session.screenWidth || 0;
        if (width < 768) sourceKey = "Mobile";
        else if (width < 1024) sourceKey = "Tablet";
        else sourceKey = "Desktop";
        break;
      }
    }

    if (!sourceData[sourceKey]) {
      sourceData[sourceKey] = { sessions: 0, conversions: 0, engaged: 0 };
    }

    sourceData[sourceKey].sessions++;
    if (session.signedUp || session.contacted) {
      sourceData[sourceKey].conversions++;
    }
    // Engaged = viewed 2+ pages or had a conversion
    const pageCount = session.pageSessions?.length || 0;
    if (pageCount > 1 || session.signedUp || session.contacted) {
      sourceData[sourceKey].engaged++;
    }
  });

  return Object.entries(sourceData)
    .map(([source, data]) => ({
      source,
      sessions: data.sessions,
      engagedSessionRate: data.sessions > 0 ? data.engaged / data.sessions : 0,
      ctaConversionRate:
        data.sessions > 0 ? data.conversions / data.sessions : 0,
    }))
    .sort((a, b) => b.sessions - a.sessions);
}

// ============================================================================
// MAIN ACQUISITION PAGE COMPONENT
// ============================================================================

export const AcquisitionPage: React.FC = () => {
  const { savedSegments } = useFilters();
  const {
    sessions,
    sessionsLoading: loading,
    sessionsError: error,
  } = useDataStore();
  const theme = useTheme();
  const [view, setView] = useState<AcquisitionView>("country");
  const [sortKey, setSortKey] = useState<SortKey>("sessions");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  // Local state for segment filter
  const [activeSegment, setActiveSegment] = useState<Segment>(
    savedSegments[0] || DEFAULT_SEGMENTS[0]
  );

  // Filter sessions by segment
  const filteredSessions = useMemo(
    () => filterSessionsBySegment(sessions, activeSegment),
    [sessions, activeSegment]
  );

  const sources = useMemo(() => {
    const extracted = extractAcquisitionDataFromSessions(
      filteredSessions,
      view
    );

    return extracted.sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      const multiplier = sortDirection === "desc" ? -1 : 1;
      return (aVal - bVal) * multiplier;
    });
  }, [filteredSessions, view, sortKey, sortDirection]);

  const chartData = useMemo(() => {
    return sources.slice(0, 8).map((s) => ({
      name: s.source.length > 12 ? s.source.substring(0, 12) + "..." : s.source,
      sessions: s.sessions,
      conversions: Math.round(s.sessions * s.ctaConversionRate),
    }));
  }, [sources]);

  const pieData = useMemo(() => {
    const top5 = sources.slice(0, 5);
    const others = sources.slice(5).reduce((sum, s) => sum + s.sessions, 0);

    const result = top5.map((s) => ({
      name: s.source,
      value: s.sessions,
    }));

    if (others > 0) {
      result.push({ name: "Others", value: others });
    }

    return result;
  }, [sources]);

  const maxSessions = useMemo(
    () => Math.max(...sources.map((s) => s.sessions), 1),
    [sources]
  );

  const handleViewChange = (
    _event: React.MouseEvent<HTMLElement>,
    newView: AcquisitionView | null
  ) => {
    if (newView) {
      setView(newView);
    }
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === "desc" ? "asc" : "desc");
    } else {
      setSortKey(key);
      setSortDirection("desc");
    }
  };

  const getViewLabel = (v: AcquisitionView): string => {
    switch (v) {
      case "source":
        return "Source";
      case "country":
        return "Country";
      case "device":
        return "Device";
      default:
        return v;
    }
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Paper sx={{ mb: 3, p: 2 }}>
        <Stack spacing={2}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Traffic Acquisition
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Analyze where your traffic comes from and which sources drive
                the best quality users.
              </Typography>
            </Box>
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={handleViewChange}
              size="small"
            >
              <ToggleButton value="country">Country</ToggleButton>
              <ToggleButton value="device">Device</ToggleButton>
              <ToggleButton value="source">Source</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {/* Segment Selector */}
          <SingleSegmentSelector
            selectedSegment={activeSegment}
            onSegmentChange={setActiveSegment}
          />
        </Stack>
      </Paper>

      <Grid container spacing={3}>
        {/* Charts */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2, height: 350 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
              Sessions by {getViewLabel(view)}
            </Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={280} />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis
                    dataKey="name"
                    type="category"
                    width={100}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip />
                  <Bar
                    dataKey="sessions"
                    fill={theme.palette.primary.main}
                    name="Sessions"
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2, height: 350 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
              Traffic Distribution
            </Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={280} />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ name, percent }) =>
                      `${name}: ${(percent * 100).toFixed(0)}%`
                    }
                    labelLine={false}
                  >
                    {pieData.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={CHART_COLORS[index % CHART_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* Table */}
        <Grid size={12}>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{getViewLabel(view)}</TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortKey === "sessions"}
                      direction={
                        sortKey === "sessions" ? sortDirection : "desc"
                      }
                      onClick={() => handleSort("sessions")}
                    >
                      Sessions
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortKey === "engagedSessionRate"}
                      direction={
                        sortKey === "engagedSessionRate"
                          ? sortDirection
                          : "desc"
                      }
                      onClick={() => handleSort("engagedSessionRate")}
                    >
                      Engaged Rate
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortKey === "ctaConversionRate"}
                      direction={
                        sortKey === "ctaConversionRate" ? sortDirection : "desc"
                      }
                      onClick={() => handleSort("ctaConversionRate")}
                    >
                      Conversion Rate
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading
                  ? Array(8)
                      .fill(null)
                      .map((_, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <Skeleton />
                          </TableCell>
                          <TableCell>
                            <Skeleton />
                          </TableCell>
                          <TableCell>
                            <Skeleton />
                          </TableCell>
                          <TableCell>
                            <Skeleton />
                          </TableCell>
                        </TableRow>
                      ))
                  : sources.map((source) => (
                      <SourceRow
                        key={source.source}
                        source={source}
                        maxSessions={maxSessions}
                      />
                    ))}
              </TableBody>
            </Table>

            {!loading && sources.length === 0 && (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography color="text.secondary">
                  No acquisition data available
                </Typography>
              </Box>
            )}
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );
};
